import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Input, PasswordInput, Checkbox } from '@medikei/ui';
import { authApi, doctorsApi } from '@/api/http-client';
import {
  Stethoscope,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  ArrowRight,
  ArrowLeft,
  Zap,
  FileText,
  Wallet,
  Lock,
  Sparkles,
} from 'lucide-react';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { cn } from '@/utils/cn';

const doctorRegisterSchema = z
  .object({
    name: z
      .string()
      .min(3, 'Nome completo deve ter pelo menos 3 caracteres (conforme registro oficial no CRM)')
      .max(100, 'Nome completo não pode exceder 100 caracteres'),
    email: z.string().min(1, 'E-mail profissional ou pessoal é obrigatório').email('Endereço de e-mail inválido (ex.: doutor@email.com)'),
    phone: z.string().min(1, 'Telefone / WhatsApp com DDD é obrigatório'),
    cpf: z
      .string()
      .min(1, 'CPF é obrigatório para emissão de receitas e validação no CFM')
      .refine((val) => /^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(val) || val.replace(/\D/g, '').length === 11, 'CPF incompleto ou inválido. Digite os 11 números do seu CPF'),
    crm: z.string().min(1, 'Informe o número do seu CRM'),
    crmState: z.string().length(2, 'Informe a UF do conselho com 2 letras (ex: SP, RJ, MG)'),
    password: z.string().min(8, 'A senha deve conter no mínimo 8 caracteres'),
    confirmPassword: z.string().min(1, 'Confirmação de senha é obrigatória'),
    termsConsent: z.boolean().refine((val) => val === true, 'Você deve concordar com os Termos de Uso Profissional e a Política de Privacidade para continuar'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'As senhas digitadas não coincidem. Verifique e tente novamente',
    path: ['confirmPassword'],
  })
  .refine((data) => /^\(\d{2}\)\s?\d{4,5}-\d{4}$/.test(data.phone) || data.phone.replace(/\D/g, '').length >= 10, {
    message: 'Telefone inválido. Formato esperado: (XX) 9XXXX-XXXX',
    path: ['phone'],
  });

type DoctorRegisterForm = z.infer<typeof doctorRegisterSchema>;

export function DoctorRegisterPage() {
  const navigate = useNavigate();
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  // Real-time CRM Validation State
  const [isValidatingCrm, setIsValidatingCrm] = useState(false);
  const [crmValidationResult, setCrmValidationResult] = useState<{
    isValid: boolean;
    isManualReviewRequired?: boolean;
    doctorName?: string;
    crm?: string;
    crmState?: string;
    situation?: string;
    specialties?: string[];
    errorMessage?: string;
    infoMessage?: string;
    keyUsedName?: string;
  } | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<DoctorRegisterForm>({
    resolver: zodResolver(doctorRegisterSchema),
    defaultValues: {
      termsConsent: false,
    },
  });

  const crmValue = (watch('crm') || '') as string;
  const crmStateValue = (watch('crmState') || '') as string;

  const formatPhone = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 2) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
  };

  const formatCpf = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
  };

  // Live CRM verification against CFM / CREMESP / Consulta CRM (dinâmico e automático)
  const lastVerifiedKeyRef = useRef<string>('');
  const activeRequestSeqRef = useRef<number>(0);

  const handleVerifyCrm = useCallback(
    async (cleanCrm: string, cleanUf: string) => {
      if (!cleanCrm || cleanCrm.length < 4 || !cleanUf || cleanUf.length !== 2) {
        setIsValidatingCrm(false);
        setCrmValidationResult(null);
        return;
      }

      const currentKey = `${cleanCrm}_${cleanUf}`;
      if (currentKey === lastVerifiedKeyRef.current) {
        return;
      }

      lastVerifiedKeyRef.current = currentKey;
      const requestSeq = ++activeRequestSeqRef.current;

      setIsValidatingCrm(true);
      setCrmValidationResult(null);
      setApiError(null);

      try {
        const validationPromise = doctorsApi.validateCrm(cleanCrm, cleanUf);
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('TIMEOUT')), 8000)
        );

        const result = (await Promise.race([validationPromise, timeoutPromise])) as any;

        if (requestSeq !== activeRequestSeqRef.current) {
          return;
        }

        setCrmValidationResult(result);

        if (result.isValid && result.doctorName) {
          // Auto-fill full name with official CFM registry record
          setValue('name', result.doctorName, { shouldValidate: true });
        }

        if (result.crm) {
          setValue('crm', result.crm, { shouldValidate: true });
        }

        if (result.crmState) {
          setValue('crmState', result.crmState.toUpperCase(), { shouldValidate: true });
        }

        if (result.specialties && Array.isArray(result.specialties) && result.specialties.length > 0) {
          try {
            localStorage.setItem('medikei_initial_specialties', JSON.stringify(result.specialties));
          } catch {
            // ignore quota error
          }
        }
      } catch (err: any) {
        if (requestSeq !== activeRequestSeqRef.current) return;

        if (err?.message === 'TIMEOUT') {
          setCrmValidationResult({
            isValid: true,
            isManualReviewRequired: true,
            crm: cleanCrm,
            crmState: cleanUf,
            situation: 'Aguardando validação',
            infoMessage:
              'A verificação externa demorou mais que o esperado. Preencha seu nome e prossiga.',
          });
        } else {
          setCrmValidationResult({
            isValid: false,
            errorMessage:
              err?.message ||
              'Erro ao conectar à base do Conselho Regional de Medicina. Tente novamente.',
          });
        }
      } finally {
        if (requestSeq === activeRequestSeqRef.current) {
          setIsValidatingCrm(false);
        }
      }
    },
    [setValue]
  );

  // Disparo automático e sincronizado ao preencher CRM e UF
  useEffect(() => {
    const cleanCrm = crmValue.replace(/\D/g, '').trim();
    const cleanUf = crmStateValue.trim().toUpperCase();

    if (cleanCrm.length >= 4 && cleanUf.length === 2) {
      const timer = setTimeout(() => {
        handleVerifyCrm(cleanCrm, cleanUf);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      lastVerifiedKeyRef.current = '';
      setIsValidatingCrm(false);
      setCrmValidationResult(null);
    }
    return undefined;
  }, [crmValue, crmStateValue, handleVerifyCrm]);

  const onSubmit = async (data: DoctorRegisterForm) => {
    setApiError(null);
    setIsSubmitting(true);

    try {
      await authApi.registerDoctor({
        name: data.name.trim(),
        email: data.email.trim(),
        password: data.password,
        phone: data.phone,
        cpf: data.cpf.replace(/\D/g, '').trim(),
        crm: data.crm.replace(/\D/g, '').trim(),
        crmState: data.crmState.trim().toUpperCase(),
        acceptTerms: data.termsConsent,
      });

      setRegistrationSuccess(true);
    } catch (err: any) {
      setApiError(err?.message || 'Erro ao realizar cadastro médico. Verifique os dados e tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (registrationSuccess) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-4">
        <div className="max-w-md w-full p-8 rounded-3xl border border-border bg-card shadow-2xl text-center space-y-5">
          <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center mx-auto shadow-xs">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-extrabold text-foreground tracking-tight">
            Cadastro Médico Criado!
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Sua conta médica foi criada com sucesso na Medikei. Agora faça login para enviar seus documentos comprobatórios
            (CRM, Diploma e RG/CNH) e ativar seu atendimento e prescrição digital.
          </p>
          <Button
            onClick={() => navigate('/login')}
            className="w-full h-11 rounded-xl font-bold shadow-md shadow-primary/20 gap-2 cursor-pointer"
          >
            <span>Fazer Login e Enviar Documentos</span>
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground selection:bg-primary/20 selection:text-primary">
      {/* Top Header */}
      <header className="h-16 sm:h-20 border-b border-border/60 bg-background/80 backdrop-blur-md px-4 sm:px-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/" className="flex items-center">
            <img src="/logo.png" alt="Medikei" className="h-8 sm:h-9 w-auto dark:brightness-110" />
          </Link>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-primary/10 text-primary border border-primary/20">
            <Stethoscope className="w-3.5 h-3.5" />
            <span>Área Médica</span>
          </span>
        </div>

        <div className="flex items-center gap-3">
          <ThemeToggle />
          <Link
            to="/para-medicos"
            className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors px-3 py-1.5"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>Página do Médico</span>
          </Link>
          <Link
            to="/login"
            className="text-xs sm:text-sm font-semibold text-foreground/80 hover:text-primary transition-colors px-3 py-1.5"
          >
            Já tem conta? Entrar
          </Link>
        </div>
      </header>

      {/* Main Content: Split View on Desktop */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Column: Benefits & Value Proposition */}
          <div className="lg:col-span-5 space-y-6 lg:sticky lg:top-28">
            <div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-primary/10 text-primary mb-3">
                <Sparkles className="w-3.5 h-3.5" />
                <span>Credenciamento Profissional</span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground leading-tight">
                Cadastre-se na maior rede integrada de saúde digital
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground mt-3 leading-relaxed">
                Leve seu consultório para onde estiver, atenda pacientes sob demanda e emita receitas com validade jurídica nacional.
              </p>
            </div>

            {/* Feature Bullets */}
            <div className="space-y-4 pt-2">
              <div className="p-4 rounded-2xl border border-border bg-card shadow-xs flex items-start gap-3.5">
                <div className="p-2 rounded-xl bg-primary/10 text-primary shrink-0 mt-0.5">
                  <Zap className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-foreground">Plantão Express 24/7</h4>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                    Fique online quando quiser e atenda pacientes com triagem prévia inteligente de IA.
                  </p>
                </div>
              </div>

              <div className="p-4 rounded-2xl border border-border bg-card shadow-xs flex items-start gap-3.5">
                <div className="p-2 rounded-xl bg-primary/10 text-primary shrink-0 mt-0.5">
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-foreground">Prescrição Digital Integrada (Memed)</h4>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                    Assinatura digital ICP-Brasil sem custos adicionais com envio direto via WhatsApp.
                  </p>
                </div>
              </div>

              <div className="p-4 rounded-2xl border border-border bg-card shadow-xs flex items-start gap-3.5">
                <div className="p-2 rounded-xl bg-primary/10 text-primary shrink-0 mt-0.5">
                  <Wallet className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-foreground">Zero Mensalidades & Saques via PIX</h4>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                    Sem taxas fixas de adesão. Acompanhe seus repasses na carteira e transfira via PIX.
                  </p>
                </div>
              </div>

              <div className="p-4 rounded-2xl border border-border bg-card shadow-xs flex items-start gap-3.5">
                <div className="p-2 rounded-xl bg-primary/10 text-primary shrink-0 mt-0.5">
                  <Lock className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-foreground">Respaldo Jurídico & CFM</h4>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                    Prontuário eletrônico unificado em total conformidade com a Resolução CFM nº 2.314/2022 e LGPD.
                  </p>
                </div>
              </div>
            </div>

            {/* Switch to patient registration */}
            <div className="p-4 rounded-2xl bg-muted/40 border border-border/80 flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Não é médico?</span>
              <Link to="/register" className="font-bold text-primary hover:underline">
                Criar conta de paciente &rarr;
              </Link>
            </div>
          </div>

          {/* Right Column: Doctor Registration Form */}
          <div className="lg:col-span-7">
            <div className="p-6 sm:p-8 rounded-3xl border border-border bg-card shadow-xl space-y-6">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-foreground">
                  Criar Conta Médica
                </h2>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  Preencha seus dados profissionais para validação oficial no CFM
                </p>
              </div>

              {apiError && (
                <div className="p-4 rounded-2xl bg-destructive/10 border border-destructive/20 text-destructive text-xs sm:text-sm flex items-start gap-3 animate-in fade-in duration-200 shadow-xs">
                  <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-destructive" />
                  <div className="space-y-0.5 flex-1">
                    <p className="font-bold text-xs uppercase tracking-wider">Atenção no cadastro médico</p>
                    <p className="leading-normal">{apiError}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {/* CRM Live Validation Card */}
                <div className="p-4 rounded-2xl bg-muted/40 border border-border space-y-3">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-foreground">
                    <ShieldCheck className="w-4 h-4 text-primary" />
                    <span>Validação Oficial de Registro Médico (CFM)</span>
                  </div>

                  <div className="grid grid-cols-3 gap-2.5">
                    <div className="col-span-2">
                      <Input
                        label="Número do CRM"
                        placeholder="Ex.: 123456"
                        autoComplete="off"
                        error={errors.crm?.message}
                        {...register('crm')}
                      />
                    </div>
                    <div>
                      <Input
                        label="UF"
                        placeholder="SP"
                        maxLength={2}
                        autoComplete="off"
                        className="uppercase text-center font-bold"
                        error={errors.crmState?.message}
                        {...register('crmState', {
                          onChange: (e) => {
                            e.target.value = e.target.value.toUpperCase();
                          },
                        })}
                      />
                    </div>
                  </div>

                  {/* Dynamic Status / Feedback da Consulta ao CFM */}
                  <div
                    className={cn(
                      'p-3.5 rounded-xl border text-xs flex items-start gap-2.5 transition-all duration-200',
                      isValidatingCrm && crmValue.replace(/\D/g, '').length >= 4 && crmStateValue.trim().length === 2
                        ? 'bg-blue-50/80 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-300'
                        : crmValidationResult?.isValid && crmValidationResult.isManualReviewRequired
                        ? 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-900 text-amber-800 dark:text-amber-300'
                        : crmValidationResult?.isValid
                        ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-900 text-emerald-800 dark:text-emerald-300'
                        : crmValidationResult && !crmValidationResult.isValid
                        ? 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-900 text-red-800 dark:text-red-300'
                        : 'bg-background border-dashed border-border text-muted-foreground'
                    )}
                  >
                    {isValidatingCrm && crmValue.replace(/\D/g, '').length >= 4 && crmStateValue.trim().length === 2 ? (
                      <Loader2 className="w-4 h-4 animate-spin text-primary shrink-0 mt-0.5" />
                    ) : crmValidationResult?.isValid && crmValidationResult.isManualReviewRequired ? (
                      <ShieldCheck className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    ) : crmValidationResult?.isValid ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                    ) : crmValidationResult && !crmValidationResult.isValid ? (
                      <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                    ) : (
                      <ShieldCheck className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                    )}

                    <div className="flex-1">
                      {isValidatingCrm && crmValue.replace(/\D/g, '').length >= 4 && crmStateValue.trim().length === 2 ? (
                        <div>
                          <p className="font-bold text-blue-900 dark:text-blue-200">
                            Consultando regularidade do CRM no CFM / Conselho...
                          </p>
                          <p className="text-[11px] text-blue-700 dark:text-blue-300 mt-0.5">
                            Validando número e estado para preencher seu nome oficial automaticamente.
                          </p>
                        </div>
                      ) : crmValidationResult?.isValid && crmValidationResult.isManualReviewRequired ? (
                        <div>
                          <p className="font-bold text-amber-900 dark:text-amber-200">
                            CRM {crmValue}/{crmStateValue?.toUpperCase()} registrado para análise
                          </p>
                          <p className="mt-0.5 leading-relaxed text-[11px]">
                            {crmValidationResult.infoMessage ||
                              'Preencha seu nome completo e prossiga. O documento do CRM será validado pela equipe na ativação da conta.'}
                          </p>
                        </div>
                      ) : crmValidationResult?.isValid ? (
                        <div>
                          <p className="font-bold">
                            ✓ CRM {crmValue}/{crmStateValue?.toUpperCase()} Ativo e Regular no CFM
                          </p>
                          {crmValidationResult.doctorName && (
                            <p className="mt-0.5 leading-relaxed text-[11px]">
                              Profissional: <strong>{crmValidationResult.doctorName}</strong>{' '}
                              {crmValidationResult.situation ? `(Situação: ${crmValidationResult.situation})` : ''}
                            </p>
                          )}
                          {crmValidationResult.specialties && crmValidationResult.specialties.length > 0 && (
                            <p className="mt-0.5 leading-relaxed text-[11px] text-emerald-700 dark:text-emerald-300">
                              Especialidade(s) localizada(s): <strong>{crmValidationResult.specialties.join(', ')}</strong>
                            </p>
                          )}
                        </div>
                      ) : crmValidationResult && !crmValidationResult.isValid ? (
                        <div>
                          <p className="font-bold">Atenção no CRM informado</p>
                          <p className="mt-0.5 leading-relaxed text-[11px]">
                            {crmValidationResult.errorMessage || 'CRM não localizado ou com pendência junto ao conselho regional.'}
                          </p>
                        </div>
                      ) : (
                        <p className="text-[11px] leading-relaxed">
                          A busca e validação do CRM são realizadas <strong>automaticamente</strong> assim que você preencher o número do CRM e a UF de 2 letras.
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Nome Completo */}
                <div className="space-y-1.5">
                  <Input
                    label="Nome Completo (Conforme Registro CRM)"
                    placeholder="Seu nome completo"
                    autoComplete="name"
                    error={errors.name?.message}
                    {...register('name')}
                  />
                  {crmValidationResult?.doctorName && (
                    <div className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                      <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                      <span>Nome oficial preenchido automaticamente conforme registro do CRM</span>
                    </div>
                  )}
                </div>

                {/* Email Profissional */}
                <Input
                  label="Email Profissional / Pessoal"
                  type="email"
                  placeholder="doutor@email.com"
                  autoComplete="email"
                  error={errors.email?.message}
                  {...register('email')}
                />

                {/* Telefone e CPF */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input
                    label="Telefone / WhatsApp"
                    type="tel"
                    placeholder="(00) 00000-0000"
                    autoComplete="tel"
                    error={errors.phone?.message}
                    {...register('phone', {
                      onChange: (e) => {
                        e.target.value = formatPhone(e.target.value);
                      },
                    })}
                  />

                  <Input
                    label="CPF (Validação CFM / Receita Digital)"
                    type="text"
                    placeholder="000.000.000-00"
                    error={errors.cpf?.message}
                    {...register('cpf', {
                      onChange: (e) => {
                        e.target.value = formatCpf(e.target.value);
                      },
                    })}
                  />
                </div>

                {/* Senha e Confirmação */}
                <PasswordInput
                  label="Senha"
                  placeholder="••••••••"
                  autoComplete="new-password"
                  error={errors.password?.message}
                  {...register('password')}
                />

                <PasswordInput
                  label="Confirmar Senha"
                  placeholder="••••••••"
                  autoComplete="new-password"
                  error={errors.confirmPassword?.message}
                  {...register('confirmPassword')}
                />

                {/* Termos de Uso e Privacidade */}
                <div className="space-y-3 pt-2">
                  <Checkbox
                    id="doctor-terms"
                    {...register('termsConsent')}
                    label={
                      <span className="text-xs text-muted-foreground leading-tight">
                        Declaro que sou médico com habilitação regular e aceito os{' '}
                        <Link to="/termos" className="text-primary hover:underline font-semibold" target="_blank">
                          Termos de Uso Profissional
                        </Link>{' '}
                        e a{' '}
                        <Link to="/privacidade" className="text-primary hover:underline font-semibold" target="_blank">
                          Política de Privacidade
                        </Link>
                        . <span className="text-destructive">*</span>
                      </span>
                    }
                  />
                  {errors.termsConsent && (
                    <p className="mt-1 text-xs text-destructive">{errors.termsConsent.message}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  className="w-full h-12 rounded-xl font-bold text-sm shadow-md shadow-primary/20 gap-2 mt-4 cursor-pointer"
                >
                  <span>Concluir Cadastro de Médico</span>
                  <ArrowRight className="w-4 h-4" />
                </Button>

                <div className="pt-2 text-center">
                  <p className="text-xs text-muted-foreground">
                    Já tem uma conta médica?{' '}
                    <Link to="/login" className="font-semibold text-primary hover:underline transition-colors">
                      Fazer login
                    </Link>
                  </p>
                </div>
              </form>

              <div className="pt-4 border-t border-border flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                <span>Sigilo médico e dados protegidos por criptografia de ponta a ponta</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DoctorRegisterPage;
